/**
 * RBAC Check Result Component
 * 展示集群权限检测结果
 * - 顶部：简洁状态统计（Banner 已有 SA/URL/时间，此处不重复）
 * - Tab: Cluster-Scoped / 各 namespace（来自 rbacDataChain）
 */

import { useState } from 'react';
import {
  StatusOK,
  StatusWarning,
  StatusError,
} from '@backstage/core-components';
import {
  Box,
  Typography,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Tabs,
  Tab,
  Paper,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CloudOffIcon from '@material-ui/icons/CloudOff';
import { ClusterCheckResult, PermissionCheckResult, RbacBinding, RbacRole } from './types';

const useStyles = makeStyles(theme => ({
  connectionError: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    background: theme.palette.error.light,
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.error.contrastText,
  },
  permissionItem: {
    fontFamily: 'monospace',
    fontSize: '0.85rem',
    padding: theme.spacing(0.5, 1),
    margin: theme.spacing(0.25, 0),
    borderRadius: 4,
  },
  allowedItem: {
    background: 'rgba(76, 175, 80, 0.1)',
    borderLeft: '3px solid #4caf50',
  },
  deniedItem: {
    background: 'rgba(244, 67, 54, 0.1)',
    borderLeft: '3px solid #f44336',
  },
  summary: {
    display: 'flex',
    gap: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  categoryHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  categoryInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  ruleItem: {
    fontFamily: 'monospace',
    fontSize: '0.82rem',
    padding: theme.spacing(0.5, 1),
    margin: theme.spacing(0.25, 0),
    borderRadius: 4,
    background: 'rgba(0,0,0,0.04)',
  },
}));

// 权限类别映射
const CATEGORY_LABELS: Record<string, string> = {
  k8sRead: 'K8s 只读权限',
  k8sManage: 'K8s 管理权限',
  tekton: 'Tekton 管理权限',
  knative: 'Knative 管理权限',
  sealedSecrets: 'SealedSecrets 权限',
};

// ── 单个权限类别 Accordion ──
const PermissionAccordion: React.FC<{ permission: PermissionCheckResult }> = ({ permission }) => {
  const classes = useStyles();

  const getStatusComponent = () => {
    switch (permission.status) {
      case 'ok': return <StatusOK>{CATEGORY_LABELS[permission.category] || permission.name}</StatusOK>;
      case 'partial': return <StatusWarning>{CATEGORY_LABELS[permission.category] || permission.name}</StatusWarning>;
      case 'missing': return <StatusError>{CATEGORY_LABELS[permission.category] || permission.name}</StatusError>;
      default: return <StatusOK>{CATEGORY_LABELS[permission.category] || permission.name}</StatusOK>;
    }
  };

  const getStatusLabel = () => {
    switch (permission.status) {
      case 'ok': return '完整';
      case 'partial': return '部分缺失';
      case 'missing': return '缺失';
      default: return '未知';
    }
  };

  const getChipColor = (): 'primary' | 'secondary' | 'default' => {
    if (permission.status === 'ok') return 'primary';
    if (permission.status === 'partial') return 'secondary';
    return 'default';
  };

  return (
    <Accordion defaultExpanded={false}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box className={classes.categoryHeader}>
          <Box className={classes.categoryInfo}>
            {getStatusComponent()}
            <Typography variant="body2" color="textSecondary">
              {permission.description}
            </Typography>
          </Box>
          <Chip label={getStatusLabel()} size="small" color={getChipColor()} />
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box width="100%">
          {permission.allowed.length > 0 && (
            <Box mb={2}>
              <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600 }}>
                ✅ 已授权权限 ({permission.allowed.length})
              </Typography>
              {permission.allowed.map((item, index) => (
                <Box key={index} className={`${classes.permissionItem} ${classes.allowedItem}`}>
                  {item}
                </Box>
              ))}
            </Box>
          )}
          {permission.denied.length > 0 && (
            <Box>
              {permission.allowed.length > 0 && <Divider style={{ marginBottom: 16 }} />}
              <Typography variant="subtitle2" gutterBottom color="error" style={{ fontWeight: 600 }}>
                ❌ 缺失权限 ({permission.denied.length})
              </Typography>
              {permission.denied.map((item, index) => (
                <Box key={index} className={`${classes.permissionItem} ${classes.deniedItem}`}>
                  {item}
                </Box>
              ))}
            </Box>
          )}
          {permission.allowed.length === 0 && permission.denied.length === 0 && (
            <Typography variant="body2" color="textSecondary">未检测到权限信息</Typography>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

// ── namespace Tab 内容：RoleBindings + Role 规则 ──
const NamespaceRbacPanel: React.FC<{
  namespace: string;
  roleBindings: RbacBinding[];
  roles: RbacRole[];
  clusterRoles: RbacRole[];
}> = ({ namespace, roleBindings, roles, clusterRoles }) => {
  const classes = useStyles();

  if (roleBindings.length === 0) {
    return (
      <Box p={3} textAlign="center">
        <Typography color="textSecondary">namespace {namespace} 下没有 RoleBinding</Typography>
      </Box>
    );
  }

  const roleMap = new Map<string, RbacRole>(roles.map(r => [`${r.namespace}/${r.name}`, r]));
  const crMap = new Map<string, RbacRole>(clusterRoles.map(r => [r.name, r]));

  return (
    <Box>
      {roleBindings.map((rb, idx) => {
        const resolved = rb.roleRef.kind === 'Role'
          ? roleMap.get(`${rb.namespace}/${rb.roleRef.name}`)
          : crMap.get(rb.roleRef.name);

        return (
          <Box key={idx} mb={2}>
            <Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: 8 }}>
              <Chip
                label={rb.kind}
                size="small"
                variant="outlined"
                style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
              />
              <Typography variant="body2" style={{ fontFamily: 'monospace', fontWeight: 600 }}>
                {rb.name}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                → {rb.roleRef.kind}/{rb.roleRef.name}
              </Typography>
            </Box>
            {resolved ? (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    <Chip
                      label={resolved.kind}
                      size="small"
                      color={resolved.kind === 'ClusterRole' ? 'primary' : 'default'}
                    />
                    <Typography variant="body2" style={{ fontFamily: 'monospace' }}>
                      {resolved.name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      · {resolved.rules.length} 条规则
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box width="100%">
                    {resolved.rules.map((rule, rIdx) => (
                      <Box key={rIdx} className={classes.ruleItem}>
                        <Typography variant="caption" display="block" style={{ fontWeight: 600 }}>
                          apiGroups: {rule.apiGroups.map(g => g || '""').join(', ')}
                        </Typography>
                        <Typography variant="caption" display="block">
                          resources: {rule.resources.join(', ')}
                        </Typography>
                        <Typography variant="caption" display="block">
                          verbs: {rule.verbs.join(', ')}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ) : (
              <Typography variant="caption" color="textSecondary" style={{ marginLeft: 8 }}>
                无法解析 {rb.roleRef.kind} "{rb.roleRef.name}"（权限不足）
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

// ── 主组件 ──
interface RbacCheckResultProps {
  result: ClusterCheckResult;
}

export const RbacCheckResult: React.FC<RbacCheckResultProps> = ({ result }) => {
  const classes = useStyles();
  const [tabIndex, setTabIndex] = useState(0);

  // 连接失败
  if (result.connectionStatus === 'error') {
    return (
      <Box className={classes.connectionError}>
        <CloudOffIcon fontSize="large" />
        <Box>
          <Typography variant="subtitle1" style={{ fontWeight: 600 }}>连接失败</Typography>
          <Typography variant="body2">
            {result.connectionError || '无法连接到集群 API Server'}
          </Typography>
          <Typography variant="caption" style={{ marginTop: 8, display: 'block' }}>
            请检查集群 URL、Token 和网络连接
          </Typography>
        </Box>
      </Box>
    );
  }

  // 统计
  const okCount = result.permissions.filter(p => p.status === 'ok').length;
  const partialCount = result.permissions.filter(p => p.status === 'partial').length;
  const missingCount = result.permissions.filter(p => p.status === 'missing').length;

  // 从 rbacDataChain 按 namespace 分组 RoleBindings
  const nsBindings = new Map<string, RbacBinding[]>();
  if (result.rbacDataChain) {
    for (const rb of result.rbacDataChain.roleBindings) {
      const ns = rb.namespace || 'default';
      if (!nsBindings.has(ns)) nsBindings.set(ns, []);
      nsBindings.get(ns)!.push(rb);
    }
  }
  const namespaces = [...nsBindings.keys()].sort();

  return (
    <Paper variant="outlined" style={{ borderRadius: 8, overflow: 'hidden' }}>
      {/* 状态统计行 */}
      <Box
        display="flex"
        alignItems="center"
        style={{ gap: 24, padding: '12px 16px', borderBottom: '1px solid rgba(0,0,0,0.08)' }}
      >
        <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
          SelfSubjectAccessReview 检测结果
        </Typography>
        <Box className={classes.summary} style={{ margin: 0 }}>
          <StatusOK>{okCount} 完整</StatusOK>
          <StatusWarning>{partialCount} 部分缺失</StatusWarning>
          <StatusError>{missingCount} 缺失</StatusError>
        </Box>
      </Box>

      {/* Tab 导航：Cluster-Scoped + 各 namespace */}
      <Tabs
        value={tabIndex}
        onChange={(_, v) => setTabIndex(v)}
        indicatorColor="primary"
        textColor="primary"
        variant="scrollable"
        scrollButtons="auto"
        style={{ borderBottom: '1px solid rgba(0,0,0,0.08)', paddingLeft: 8 }}
      >
        {/* Cluster-Scoped 始终第一个 */}
        <Tab
          label={
            <Box display="flex" alignItems="center" style={{ gap: 6 }}>
              <span>Cluster-Scoped</span>
              {result.permissions.length > 0 && (
                <Chip
                  label={result.permissions.length}
                  size="small"
                  style={{ height: 18, fontSize: '0.7rem' }}
                />
              )}
            </Box>
          }
          style={{ textTransform: 'none', minWidth: 120 }}
        />
        {/* 动态 namespace Tabs */}
        {namespaces.map((ns, idx) => (
          <Tab
            key={idx}
            label={
              <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                <span>{ns}</span>
                <Chip
                  label={nsBindings.get(ns)?.length || 0}
                  size="small"
                  variant="outlined"
                  style={{ height: 18, fontSize: '0.7rem' }}
                />
              </Box>
            }
            style={{ textTransform: 'none', minWidth: 100 }}
          />
        ))}
      </Tabs>

      {/* Tab 内容 */}
      <Box p={2}>
        {/* Cluster-Scoped：权限类别 accordion */}
        {tabIndex === 0 && (
          <>
            <Typography variant="caption" color="textSecondary" paragraph>
              基于五大类权限标准进行检测：K8s 只读、K8s 管理、Tekton 管理、Knative 管理、SealedSecrets
            </Typography>
            {result.permissions.length === 0 ? (
              <Box p={2} textAlign="center">
                <Typography color="textSecondary">
                  未检测到权限信息，请检查集群连接和 ServiceAccount 配置
                </Typography>
              </Box>
            ) : (
              result.permissions.map(permission => (
                <PermissionAccordion key={permission.category} permission={permission} />
              ))
            )}
          </>
        )}

        {/* Namespace Tabs */}
        {namespaces.map((ns, idx) =>
          tabIndex === idx + 1 && result.rbacDataChain ? (
            <NamespaceRbacPanel
              key={ns}
              namespace={ns}
              roleBindings={nsBindings.get(ns) || []}
              roles={result.rbacDataChain.roles}
              clusterRoles={result.rbacDataChain.clusterRoles}
            />
          ) : null,
        )}

      </Box>
    </Paper>
  );
};
